import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Copy, Check } from "lucide-react";
import { ToolShell } from "@/components/ToolShell";
import { buildSeo } from "@/lib/seo";
import {
  FONT_DESKTOP_DESC,
  FONT_DESKTOP_TITLE,
  FONT_MOBILE_DESC,
  FONT_MOBILE_TITLE,
  LIMITS,
  measurePx,
  truncateToPx,
} from "@/lib/serp-measure";

export const Route = createFileRoute("/tools/serp-simulator")({
  head: () =>
    buildSeo({
      title: "Google SERP Simulator — Pixel-Accurate Snippet Preview (Desktop + Mobile)",
      description:
        "Free SERP preview tool. Paste your title, meta description, and URL — see exactly how your snippet renders on desktop and mobile, with pixel-width truncation warnings.",
      path: "/tools/serp-simulator",
      ogEyebrow: "SERP Simulator",
      keywords: "google serp preview, serp simulator, title tag length checker, meta description preview",
    }),
  component: SerpSimulator,
});

function SerpSimulator() {
  const [title, setTitle] = useState("Free SEO Tools — SERP Simulator, Schema Generator & Robots Tester");
  const [desc, setDesc] = useState(
    "Free, no-signup SEO tools built by a working consultant. Pixel-accurate SERP preview, JSON-LD generator, and robots.txt tester for Googlebot, GPTBot and ClaudeBot."
  );
  const [url, setUrl] = useState("https://freelancertamal.com/tools");
  const [copied, setCopied] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => setReady(true), []);

  const measurements = useMemo(() => {
    if (!ready) return null;
    return {
      desktopTitle: measurePx(title, FONT_DESKTOP_TITLE),
      desktopDesc: measurePx(desc, FONT_DESKTOP_DESC),
      mobileTitle: measurePx(title, FONT_MOBILE_TITLE),
      mobileDesc: measurePx(desc, FONT_MOBILE_DESC),
    };
  }, [title, desc, ready]);

  const renderedDesktopTitle = ready ? truncateToPx(title, FONT_DESKTOP_TITLE, LIMITS.desktopTitlePx) : title;
  const renderedDesktopDesc = ready ? truncateToPx(desc, FONT_DESKTOP_DESC, LIMITS.desktopDescPx) : desc;
  const renderedMobileTitle = ready ? truncateToPx(title, FONT_MOBILE_TITLE, LIMITS.mobileTitlePx) : title;
  const renderedMobileDesc = ready ? truncateToPx(desc, FONT_MOBILE_DESC, LIMITS.mobileDescPx) : desc;

  let displayUrl = url;
  try {
    const u = new URL(url);
    displayUrl = `${u.hostname}${u.pathname.replace(/\/$/, "")}`.replace(/\//g, " › ");
  } catch {
    /* noop */
  }

  const copyHtml = async () => {
    const html = `<title>${title}</title>\n<meta name="description" content="${desc.replace(/"/g, "&quot;")}" />`;
    try {
      await navigator.clipboard.writeText(html);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* noop */
    }
  };

  const widthState = (px: number, max: number) =>
    px > max ? "text-destructive" : px > max * 0.95 ? "text-yellow-400" : "text-primary";

  return (
    <ToolShell
      eyebrow="SERP Simulator"
      title={<>Pixel-accurate <span className="text-gradient">Google SERP preview.</span></>}
      subtitle="Paste your title, description, and URL. See how Google renders the snippet on desktop and mobile — and get a warning the moment your title or meta description gets truncated."
      toolName="SERP Simulator"
      toolPath="/tools/serp-simulator"
      description="Free pixel-accurate Google SERP snippet preview for desktop and mobile."
      howToSteps={[
        { name: "Paste your URL", text: "Drop the canonical URL of the page you want to preview." },
        { name: "Paste the title tag", text: "The simulator measures pixel width using Google's render font (Arial 20px desktop, 16px mobile)." },
        { name: "Paste the meta description", text: "Watch the live counter — green is safe, yellow is borderline, red means Google will truncate it." },
        { name: "Copy the meta tags", text: "Click \"Copy as HTML\" and paste the title and description directly into your <head>." },
      ]}
      faqs={[
        {
          q: "Why pixel width and not character count?",
          a: "Google truncates titles based on rendered pixel width, not characters. A title of 60 'i' characters fits, but 60 'W' characters won't. The desktop limit is roughly 580px and mobile is around 560px.",
        },
        {
          q: "What font does Google actually use?",
          a: "Arial-equivalent at 20px for desktop titles and 14px for descriptions. Mobile titles render at 16px. We approximate this with HTML5 Canvas measureText, which is what most SERP tools (including Yoast and Mangools) use under the hood.",
        },
        {
          q: "Why does Google sometimes rewrite my title?",
          a: "Google rewrites titles in roughly 60% of SERPs based on query intent and on-page signals. To minimize rewrites: keep titles under the pixel limit, match user intent, avoid clickbait, and ensure your H1 closely matches the title tag.",
        },
        {
          q: "Does this tool send my data anywhere?",
          a: "No. It runs entirely in your browser — title, description, and URL never leave your device.",
        },
      ]}
      ctaTitle="Need a full on-page SEO review?"
      ctaHref="/services/digital-marketing"
      ctaLabel="See SEO services"
    >
      <div className="grid gap-10 lg:grid-cols-[1fr_1.1fr]">
        {/* Inputs */}
        <div className="space-y-5">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-primary">URL</label>
            <input
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="mt-2 w-full rounded-2xl border border-border bg-card px-5 py-3 text-sm focus:outline-none focus:border-primary/60"
              placeholder="https://example.com/page"
            />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs uppercase tracking-[0.2em] text-primary">Title tag</label>
              <div className="text-xs text-muted-foreground">
                {title.length} chars ·{" "}
                <span className={measurements ? widthState(measurements.desktopTitle, LIMITS.desktopTitlePx) : ""}>
                  {measurements?.desktopTitle ?? 0}px desktop
                </span>{" "}
                /{" "}
                <span className={measurements ? widthState(measurements.mobileTitle, LIMITS.mobileTitlePx) : ""}>
                  {measurements?.mobileTitle ?? 0}px mobile
                </span>
              </div>
            </div>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-2 w-full rounded-2xl border border-border bg-card px-5 py-3 text-sm focus:outline-none focus:border-primary/60"
            />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs uppercase tracking-[0.2em] text-primary">Meta description</label>
              <div className="text-xs text-muted-foreground">
                {desc.length} chars ·{" "}
                <span className={measurements ? widthState(measurements.desktopDesc, LIMITS.desktopDescPx) : ""}>
                  {measurements?.desktopDesc ?? 0}px desktop
                </span>
              </div>
            </div>
            <textarea
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              rows={4}
              className="mt-2 w-full rounded-2xl border border-border bg-card px-5 py-3 text-sm focus:outline-none focus:border-primary/60"
            />
          </div>

          <button
            type="button"
            onClick={copyHtml}
            className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-3 text-sm font-medium hover:bg-secondary transition"
          >
            {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Copy as HTML"}
          </button>
        </div>

        {/* Previews */}
        <div className="space-y-8">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-primary mb-3">Desktop preview</div>
            <div className="rounded-2xl border border-border bg-card p-6 max-w-[640px]">
              <div className="text-xs text-muted-foreground truncate">{displayUrl}</div>
              <div className="mt-1 text-xl text-[#8ab4f8] leading-snug font-medium">{renderedDesktopTitle}</div>
              <div className="mt-1 text-sm text-muted-foreground leading-snug">{renderedDesktopDesc}</div>
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-primary mb-3">Mobile preview</div>
            <div className="rounded-2xl border border-border bg-card p-5 max-w-[380px]">
              <div className="text-[11px] text-muted-foreground truncate">{displayUrl}</div>
              <div className="mt-1 text-base text-[#8ab4f8] leading-snug font-medium">{renderedMobileTitle}</div>
              <div className="mt-1 text-[13px] text-muted-foreground leading-snug">{renderedMobileDesc}</div>
            </div>
          </div>
        </div>
      </div>
    </ToolShell>
  );
}